import { addDays, addYears, endOfDay, format, startOfDay, subYears } from 'date-fns';

import { NativeDateService } from '@ui-kitten/components';

export const datePickerMinDate = subYears(new Date(), 100);

export const datePickerMaxDate = endOfDay(addYears(new Date(), 20));

export const datePickerService = new NativeDateService('en', { format: 'MM/DD/YYYY' });

export const dateTimePlaceholder = 'mm/dd/yyyy';

export const dateDefaultFormat = 'MM/dd/yyyy';

export const currentYear = new Date().getFullYear();

export function getBestDate(
	from: Date | undefined,
	to: Date | undefined,
	isStartDate?: boolean
): Date {
	const defaultDate = new Date();

	if (isStartDate) {
		if (from) {
			return startOfDay(from);
		} else if (to) {
			return startOfDay(to);
		}

		return startOfDay(defaultDate);
	}

	if (to) {
		return endOfDay(to);
	} else if (from) {
		return endOfDay(from);
	}

	return endOfDay(defaultDate);
}

export function isIsoDate(str: string): boolean {
	return /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/.test(str);
}

export function dateToUTC(date: Date | string | null | undefined, empty: string = ''): string {
	if (!date) {
		return empty;
	}

	const dateValue = date instanceof Date ? date : new Date(date);

	return new Date(dateValue.setUTCHours(0)).toISOString();
}

export function localDateFormat(
	date: Date | string | null | undefined,
	empty: string = ''
): string {
	if (!date) {
		return empty;
	}

	const dateValue = date instanceof Date ? date : new Date(date);

	return format(dateValue, 'P');
}

export function localDateTimeFormat(
	date: Date | string | null | undefined,
	empty: string = ''
): string {
	if (!date) {
		return empty;
	}

	const dateValue = date instanceof Date ? date : new Date(date);

	return format(dateValue, 'Pp');
}

/**
 * Small helper to left-pad a value to 2 characters without relying on String.padStart (for older lib targets).
 */
function pad2(value: string | number): string {
	return ('0' + value).slice(-2);
}

/**
 * Formats a given date using a specified pattern or the default date format.
 * Uses UTC to ensure consistent date display across all timezones.
 *
 * @param {Date | string | null | undefined} date - The date to be formatted.
 * @param {string} [pattern] - The format pattern to be used.
 * @returns {string} The formatted date string, or an empty string if no date is provided.
 */
export function dateFormat(date: Date | string | null | undefined, pattern?: string): string {
	if (!date) {
		return '';
	}

	// If it's a string, extract the date portion directly
	if (typeof date === 'string') {
		// "2025-12-19T21:15:02.214-08:00" → "2025-12-19"
		// "2025-09-04T02:26:54.174Z" → "2025-09-04"
		const datePart = date.split('T')[0];
		const [year, month, day] = datePart.split('-');

		// Handle common patterns
		if (!pattern || pattern === 'MM/dd/yyyy' || pattern === 'P') {
			return `${month}/${day}/${year}`;
		}

		return `${month}/${day}/${year}`;
	}

	// Fallback for Date objects
	const dt = date;
	const month = pad2(dt.getMonth() + 1);
	const day = pad2(dt.getDate());
	const year = dt.getFullYear();

	return `${month}/${day}/${year}`;
}

/**
 * Formats a given date to a detailed date and time format in UTC.
 * Shows both date and time as they appear in UTC.
 *
 * @param {Date | string | null | undefined} date - The date to be formatted.
 * @param {string} empty - The default string to return if no date is provided.
 * @returns {string} The formatted date and time string in UTC, or the empty string if no date is provided.
 */
export function dateTimeFormat(date: Date | string | null | undefined, empty: string): string {
	if (!date) {
		return empty;
	}

	const dt = new Date(date);

	// Extract UTC components
	const month = pad2(dt.getUTCMonth() + 1);
	const day = pad2(dt.getUTCDate());
	const year = dt.getUTCFullYear();
	const hours = dt.getUTCHours();
	const minutes = pad2(dt.getUTCMinutes());
	const seconds = pad2(dt.getUTCSeconds());
	const ampm = hours >= 12 ? 'PM' : 'AM';
	const hours12 = pad2(hours % 12 || 12);

	return `${month}/${day}/${year}, ${hours12}:${minutes}:${seconds} ${ampm}`;
}

/**
 *
 * Get next nth day to UTC format without timezone value.
 * @param {number} daysAdded
 * @param {Date} [date] - Today's date
 * @return {string} string
 *
 */

export function startOfNthDayUTC(
	daysAdded: number,
	date: Date | string = new Date() || ''
): string {
	const dateValue = date instanceof Date ? date : new Date(date);
	const startDay = startOfDay(dateValue);
	const daysAddedToDate = addDays(startDay, daysAdded);

	return dateToUTC(daysAddedToDate);
}
